/**
 * Conversores HTML <-> Markdown para TipTap.
 * Guardamos en Markdown (requisito) y al cargar reconvertimos a HTML para TipTap.
 */
import TurndownService from 'turndown';

const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
});

// Soporte para checklists (task-list de TipTap genera <ul data-type="taskList">)
turndown.addRule('taskList', {
  filter: (node) => node.nodeName === 'UL' && node.getAttribute('data-type') === 'taskList',
  replacement: (content) => content,
});
turndown.addRule('taskItem', {
  filter: (node) => node.nodeName === 'LI' && node.getAttribute('data-type') === 'taskItem',
  replacement: (content, node) => {
    const checked = (node as HTMLElement).getAttribute('data-checked') === 'true';
    return `- [${checked ? 'x' : ' '}] ${content.trim()}\n`;
  },
});

turndown.addRule('tables', {
  filter: 'table',
  replacement: (_, node) => {
    const table = node as HTMLTableElement;
    const rows = Array.from(table.rows);
    if (rows.length === 0) return '';
    const head = rows[0];
    const body = rows.slice(1);
    const headers = Array.from(head.cells).map((c) => (c.textContent ?? '').trim());
    const sep = headers.map(() => '---');
    const bodyMd = body
      .map((r) => Array.from(r.cells).map((c) => (c.textContent ?? '').trim()).join(' | '))
      .map((l) => `| ${l} |`)
      .join('\n');
    return `\n\n| ${headers.join(' | ')} |\n| ${sep.join(' | ')} |\n${bodyMd}\n\n`;
  },
});

export function htmlToMarkdown(html: string): string {
  return turndown.turndown(html);
}

/**
 * Markdown -> HTML naive: TipTap soporta Markdown via paste, así que para
 * cargar contenido inicial usamos un parser simple que convierte a HTML
 * suficientemente bien para el editor. Para render público usamos react-markdown.
 */
export function markdownToHtmlBasic(md: string): string {
  // Conversión mínima: párrafos, headings, bold, italic, listas, code, links.
  // Para casos complejos el editor maneja paste como markdown.
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^#{1,6}\s+/.test(line)) {
      const level = line.match(/^#+/)![0].length;
      out.push(`<h${level}>${inline(escape(line.replace(/^#+\s+/, '')))}</h${level}>`);
      i++;
    } else if (/^```/.test(line)) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(escape(lines[i]));
        i++;
      }
      i++;
      out.push(`<pre><code>${buf.join('\n')}</code></pre>`);
    } else if (/^[-*]\s+\[[ xX]\]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+\[[ xX]\]\s+/.test(lines[i])) {
        const checked = /\[[xX]\]/.test(lines[i]);
        const text = lines[i].replace(/^[-*]\s+\[[ xX]\]\s+/, '');
        items.push(
          `<li data-type="taskItem" data-checked="${checked}"><label><input type="checkbox" ${checked ? 'checked' : ''}><span></span></label><div>${inline(escape(text))}</div></li>`,
        );
        i++;
      }
      out.push(`<ul data-type="taskList">${items.join('')}</ul>`);
    } else if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(`<li>${inline(escape(lines[i].replace(/^[-*]\s+/, '')))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
    } else if (/^\s*$/.test(line)) {
      i++;
    } else {
      const buf: string[] = [];
      while (i < lines.length && !/^\s*$/.test(lines[i]) && !/^#{1,6}\s/.test(lines[i]) && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      out.push(`<p>${inline(escape(buf.join(' ')))}</p>`);
    }
  }
  return out.join('\n');
}

function inline(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
}
